/**
 * SUPPORTED COUNTRIES for structured ships-from / ships-to on user
 * profiles and destash listings.
 *
 * Not exhaustive — this is a curated list of countries that account
 * for the vast majority of indie polish collectors + makers. We can
 * add more as demand emerges. Alpha-2 ISO codes so the values are
 * stable regardless of display-name localization.
 *
 * `region` powers the grouped-checkbox UI in Settings: sellers see
 * countries organized rather than one giant alphabetical list.
 */

export type CountryRegion =
  | 'North America'
  | 'Europe'
  | 'Oceania'
  | 'Asia'
  | 'Latin America'
  | 'Africa'
  | 'Middle East';

export interface CountryInfo {
  code: string;
  name: string;
  region: CountryRegion;
}

export const SUPPORTED_COUNTRIES: CountryInfo[] = [
  // North America
  { code: 'US', name: 'United States', region: 'North America' },
  { code: 'CA', name: 'Canada', region: 'North America' },
  { code: 'MX', name: 'Mexico', region: 'North America' },
  // Europe
  { code: 'GB', name: 'United Kingdom', region: 'Europe' },
  { code: 'IE', name: 'Ireland', region: 'Europe' },
  { code: 'FR', name: 'France', region: 'Europe' },
  { code: 'DE', name: 'Germany', region: 'Europe' },
  { code: 'IT', name: 'Italy', region: 'Europe' },
  { code: 'ES', name: 'Spain', region: 'Europe' },
  { code: 'PT', name: 'Portugal', region: 'Europe' },
  { code: 'NL', name: 'Netherlands', region: 'Europe' },
  { code: 'BE', name: 'Belgium', region: 'Europe' },
  { code: 'AT', name: 'Austria', region: 'Europe' },
  { code: 'CH', name: 'Switzerland', region: 'Europe' },
  { code: 'PL', name: 'Poland', region: 'Europe' },
  { code: 'CZ', name: 'Czech Republic', region: 'Europe' },
  { code: 'RO', name: 'Romania', region: 'Europe' },
  { code: 'GR', name: 'Greece', region: 'Europe' },
  { code: 'TR', name: 'Türkiye (Turkey)', region: 'Europe' },
  { code: 'SE', name: 'Sweden', region: 'Europe' },
  { code: 'NO', name: 'Norway', region: 'Europe' },
  { code: 'FI', name: 'Finland', region: 'Europe' },
  { code: 'DK', name: 'Denmark', region: 'Europe' },
  { code: 'HU', name: 'Hungary', region: 'Europe' },
  // Oceania
  { code: 'AU', name: 'Australia', region: 'Oceania' },
  { code: 'NZ', name: 'New Zealand', region: 'Oceania' },
  // Asia
  { code: 'JP', name: 'Japan', region: 'Asia' },
  { code: 'KR', name: 'South Korea', region: 'Asia' },
  { code: 'SG', name: 'Singapore', region: 'Asia' },
  { code: 'HK', name: 'Hong Kong', region: 'Asia' },
  { code: 'PH', name: 'Philippines', region: 'Asia' },
  // Latin America
  { code: 'BR', name: 'Brazil', region: 'Latin America' },
  { code: 'AR', name: 'Argentina', region: 'Latin America' },
  { code: 'CL', name: 'Chile', region: 'Latin America' },
  // Africa / Middle East
  { code: 'ZA', name: 'South Africa', region: 'Africa' },
  { code: 'IL', name: 'Israel', region: 'Middle East' },
  { code: 'AE', name: 'United Arab Emirates', region: 'Middle East' },
];

const COUNTRY_BY_CODE: Record<string, CountryInfo> = SUPPORTED_COUNTRIES.reduce(
  (acc, c) => {
    acc[c.code] = c;
    return acc;
  },
  {} as Record<string, CountryInfo>
);

export function getCountryInfo(code: string | undefined | null): CountryInfo | undefined {
  if (!code) return undefined;
  return COUNTRY_BY_CODE[code];
}

/**
 * Country → default currency suggestion. Used by Settings to auto-fill
 * the currency picker when the user picks a ships-from country. Never
 * a hard mapping — the user can override. Every EU country resolves to
 * EUR; the rest are individually mapped or fall back to USD.
 */
export function defaultCurrencyForCountry(code: string | undefined | null): string {
  if (!code) return 'USD';
  const eu = new Set([
    'FR', 'DE', 'IT', 'ES', 'PT', 'NL', 'BE', 'AT', 'IE', 'GR',
    'FI', 'PL', 'CZ', 'HU',
  ]);
  if (eu.has(code)) return 'EUR';
  const map: Record<string, string> = {
    US: 'USD',
    CA: 'CAD',
    GB: 'GBP',
    AU: 'AUD',
    NZ: 'AUD',
    JP: 'JPY',
    TR: 'TRY',
    RO: 'RON',
    CH: 'EUR',
    SE: 'EUR',
    NO: 'EUR',
    DK: 'EUR',
  };
  return map[code] ?? 'USD';
}

/** Group countries by region for UI rendering. Stable ordering. */
export function groupCountriesByRegion(): Record<CountryRegion, CountryInfo[]> {
  const grouped: Record<string, CountryInfo[]> = {};
  for (const country of SUPPORTED_COUNTRIES) {
    if (!grouped[country.region]) grouped[country.region] = [];
    grouped[country.region].push(country);
  }
  return grouped as Record<CountryRegion, CountryInfo[]>;
}
